// ArrayFieldRenderer.jsx
import React, { useState, useEffect, useMemo } from "react";
import { getNestedValue } from "./utils";
import "./ArrayFieldRenderer.css";

/* ============================ Helpers ============================ */

const isHttpUrl = (s = "") => {
  try {
    const u = new URL(String(s));
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const isLikelyImageUrl = (url = "") =>
  /\.(png|jpe?g|webp|gif|svg)$/i.test(String(url).split("?")[0] || "");

/** Normalize API array like [{_id, name}] into [{ value, label, _raw }] */
const normalizeApiOptions = (arr, apiTitle = "name") =>
  (Array.isArray(arr) ? arr : []).map((o) => ({
    value: o?._id ?? String(o?.[apiTitle] ?? ""),
    label:
      (o && (o[apiTitle] ?? o.name ?? o.label ?? o.title)) ||
      String(o?._id ?? ""),
    _raw: o,
  }));

/* Small utility used by both editors */
const parseUploadResponseToUrl = (text, json) => {
  if (json) {
    if (json.fileUrl) return json.fileUrl;
    if (json.url) return json.url;
    if (Array.isArray(json.urls) && json.urls[0]) return json.urls[0];
    const cand = Object.values(json).find(
      (v) => typeof v === "string" && /^https?:\/\//i.test(v)
    );
    if (cand) return cand;
  }
  if (typeof text === "string" && /^https?:\/\//i.test(text.trim())) {
    return text.trim();
  }
  return "";
};

const filenameFromUrl = (u) => {
  try {
    const { pathname } = new URL(u);
    const name = pathname.split("/").pop() || "image";
    return decodeURIComponent(name.split("?")[0].split("#")[0]) || "image";
  } catch {
    return "image";
  }
};



/* ====================== Main Component ====================== */

const ArrayFieldRenderer = ({ col, formData, onChange }) => {
  const fieldKey = (col.field || "").split(".")[0];
  const items = Array.isArray(getNestedValue(formData, fieldKey))
    ? getNestedValue(formData, fieldKey)
    : [];

  const [inputState, setInputState] = useState({});
  const [apiOptionsMap, setApiOptionsMap] = useState({}); // sub.field -> [{value,label,_raw}]
  const [apiLoadingMap, setApiLoadingMap] = useState({}); // sub.field -> boolean
  const [apiErrorMap, setApiErrorMap] = useState({}); // sub.field -> string

  /* ---------- Static dropdown maps ---------- */
  const staticDropdownMaps = useMemo(() => {
    const map = {};
    (col.subFields || []).forEach((sub) => {
      if (String(sub.type || "").toLowerCase() === "dropdown" && !sub.api) {
        const opts = Array.isArray(sub.options) ? sub.options : [];
        map[sub.field] = opts.map((o) =>
          typeof o === "object" && o !== null
            ? {
                label: o.label ?? String(o.value ?? ""),
                value: o.value ?? String(o.label ?? ""),
              }
            : { label: String(o), value: String(o) }
        );
      }
    });
    return map;
  }, [col.subFields]);

  /* ---------- Load dropdown API options ---------- */
  useEffect(() => {
    let cancelled = false;

    async function loadForSub(sub) {
      const key = sub.field;
      if (!sub.api) return;

      setApiLoadingMap((m) => ({ ...m, [key]: true }));
      setApiErrorMap((m) => ({ ...m, [key]: "" }));

      try {
        const res = await fetch(sub.api);
        const text = await res.text();
        let data = [];
        try {
          const json = JSON.parse(text);
          data = Array.isArray(json) ? json : [json].filter(Boolean);
        } catch {
          data = [];
        }
        if (cancelled) return;
        const norm = normalizeApiOptions(data, sub.apiTitle || "name");
        setApiOptionsMap((m) => ({ ...m, [key]: norm }));
      } catch (e) {
        if (!cancelled) {
          setApiErrorMap((m) => ({
            ...m,
            [key]: e?.message || "Failed to load options",
          }));
          setApiOptionsMap((m) => ({ ...m, [key]: [] }));
        }
      } finally {
        if (!cancelled) setApiLoadingMap((m) => ({ ...m, [key]: false }));
      }
    }

    (col.subFields || []).forEach((sub) => {
      if (String(sub.type || "").toLowerCase() === "dropdown" && sub.api) {
        loadForSub(sub);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [col.subFields]);

  /* ---------- Defaults (prefilled) ---------- */
  useEffect(() => {
    const defaults = {};
    (col.subFields || []).forEach((sub) => {
      defaults[sub.field] = sub.prefilled !== undefined ? sub.prefilled : "";
    });
    setInputState(defaults);
  }, [col.subFields]);

  /* ---------- Common handlers ---------- */
  const handleSubFieldChange = (name, value, subType) => {
    const t = String(subType || "").toLowerCase();
    let v = value;
    if (t === "number") {
      v = value === "" ? "" : Number(value);
      if (Number.isNaN(v)) v = "";
    }
    setInputState((prev) => ({ ...prev, [name]: v }));
  };

  const isRowEmpty = () =>
    (col.subFields || []).every((f) => {
      const val = inputState[f.field];
      return val === "" || val == null;
    });

  const addItem = () => {
    if (isRowEmpty()) return; // Avoid empty rows
    const updatedItems = [...items, { ...inputState }];
    onChange(fieldKey, updatedItems);

    // Reset fields to prefilled for next entry
    const reset = {};
    (col.subFields || []).forEach((sub) => {
      reset[sub.field] = sub.prefilled ?? "";
    });
    setInputState(reset);
  };

  const removeItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    onChange(fieldKey, updated);
  };

  const setNestedValue = (obj, path, value) => {
    const parts = String(path).split(".");
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      if (typeof cur[key] !== "object" || cur[key] == null) cur[key] = {};
      cur = cur[key];
    }
    cur[parts[parts.length - 1]] = value;
    return obj;
  };


  /* =================== ImageLink: saved cell =================== */

  const ImageLinkCell = ({ url, label }) => {
    const [broken, setBroken] = React.useState(false);
    if (!url) return <span style={{ color: "#888" }}>—</span>;
    const isHttp = isHttpUrl(url);

    return (
      <div className="image-preview-container" style={{ maxWidth: 240 }}>
        {isHttp && !broken ? (
          <img
            src={url}
            alt={label || "Image"}
            className="image-preview"
            style={{
              maxWidth: "100%",
              maxHeight: 140,
              borderRadius: 8,
              objectFit: "contain",
              background: "#fafafa",
            }}
            onError={() => setBroken(true)}
            onLoad={() => setBroken(false)}
          />
        ) : null}

        {(broken || !isHttp) && (
          <div style={{ color: "#666", fontSize: 12, marginBottom: 6 }}>
            {broken ? "(Image failed to load)" : "Link"}
          </div>
        )}

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#2563eb", fontSize: 13 }}
        >
          View Full Image
        </a>
      </div>
    );
  };

  /* =================== ImageLink: editor (URL-only) =================== */
  const ImageLinkEditor = ({ sub }) => {
    const saved = String(inputState[sub.field] ?? "");
    const [urlInput, setUrlInput] = useState(saved);
    const [error, setError] = useState("");

    useEffect(() => {
      setUrlInput(saved);
      setError("");
    }, [saved]);

    const normalizeUrl = (u) => u.trim();

    const validateUrl = (u) => {
      if (!u) return { ok: true };
      if (!/^https?:\/\//i.test(u))
        return { ok: false, msg: "Use http(s) URL" };
      // Optional: restrict to likely image extensions (remove if you allow CDN URLs without ext)
      if (!isLikelyImageUrl(u)) {
        // allow CDNs without extension; warn softly but don’t hard-block
        return { ok: true, warn: "URL may not point directly to an image" };
      }
      return { ok: true };
    };

    const apply = () => {
      const u = normalizeUrl(urlInput);
      const { ok, msg } = validateUrl(u);
      if (!ok) {
        setError(msg || "Invalid URL");
        return;
      }
      setError("");
      handleSubFieldChange(sub.field, u, sub.type);
    };

    const clear = () => {
      setUrlInput("");
      setError("");
      handleSubFieldChange(sub.field, "", sub.type);
    };

    const showImg = (src) => (
      <img
        src={src}
        alt={sub.label || sub.field}
        style={{
          maxHeight: 90,
          maxWidth: 180,
          objectFit: "contain",
          borderRadius: 8,
          background: "#fafafa",
        }}
        onError={(e) => {
          // If it can’t render (CORS/format), fall back to link
          const a = document.createElement("a");
          a.href = src;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.textContent = "Open image";
          e.currentTarget.replaceWith(a);
        }}
      />
    );

    return (
      <div className="imageLink-editor">
        {/* Current saved value preview */}
        {saved ? (
          <div style={{ marginBottom: 6 }}>
            {isHttpUrl(saved) ? (
              showImg(saved)
            ) : (
              <a href={saved} target="_blank" rel="noopener noreferrer">
                View current
              </a>
            )}
          </div>
        ) : null}

        {/* URL input only */}
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 6,
            alignItems: "center",
          }}
        >
          <input
            className="form-input"
            type="url"
            placeholder={sub.placeholder || "Paste image URL…"}
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onBlur={apply} // commit on blur
            style={{ minWidth: 260 }}
          />
          <button
            type="button"
            className="btn-editformSubarray-add"
            onClick={apply}
            disabled={!urlInput.trim()}
            title="Save URL"
          >
            Save URL
          </button>
          <button
            type="button"
            className="btn-editformSubarray-remove"
            onClick={clear}
            title="Clear"
          >
            Clear
          </button>
        </div>

        {error && (
          <div style={{ color: "#b00020", fontSize: 12, marginTop: 6 }}>
            {error}
          </div>
        )}
      </div>
    );
  };

  /* =================== Render saved cell values =================== */

const renderSavedValue = (sub, rawValFromItem) => {
  const type = String(sub.type || "text").toLowerCase();

  if (type === "dropdown") {
    const rawVal = rawValFromItem ?? ""; // keep arg for backwards compat
    const nestedVal =
      typeof rawVal === "undefined"
        ? getNestedValue(rawValFromItem, sub.field) // if you plan to pass the whole item
        : rawVal;

    const opts = sub.api
      ? apiOptionsMap[sub.field] || []
      : staticDropdownMaps[sub.field] || [];
    const match =
      opts.find((o) => String(o.value) === String(nestedVal)) ||
      opts.find((o) => String(o.label) === String(nestedVal));
    return match ? match.label : String(nestedVal ?? "");
  }

  if (type === "imagelink") {
    const url = String(rawValFromItem || "");
    return <ImageLinkCell url={url} label={sub.label || sub.field} />;
  }

  // default (read nested by path)
  const v =
    typeof rawValFromItem === "undefined"
      ? getNestedValue(rawValFromItem, sub.field)
      : rawValFromItem;
  return String(v ?? "");
};


  /* =================== Editors per subfield type =================== */

  const renderEditor = (sub) => {
    const type = String(sub.type || "text").toLowerCase();
    const val = inputState[sub.field] ?? "";
    const commonProps = {
      className: "form-input",
      style: { marginRight: "5px" },
      "aria-label": sub.label || sub.field,
    };

    if (type === "dropdown") {
      const loading = !!apiLoadingMap[sub.field];
      const err = apiErrorMap[sub.field];
      const opts = sub.api
        ? apiOptionsMap[sub.field] || []
        : staticDropdownMaps[sub.field] || [];

      return (
        <div style={{ minWidth: 200 }}>
          <select
            {...commonProps}
            value={String(val)}
            disabled={loading}
            onChange={(e) =>
              handleSubFieldChange(sub.field, e.target.value, sub.type)
            }
          >
            {(val === "" || !val) && (
              <option value="">
                {loading
                  ? `Loading ${sub.label || sub.field}…`
                  : sub.placeholder || `Select ${sub.label || sub.field}`}
              </option>
            )}
            {opts.map((o) => (
              <option key={String(o.value)} value={String(o.value)}>
                {o.label}
              </option>
            ))}
          </select>
          {err && (
            <div style={{ color: "#b00020", fontSize: 12, marginTop: 2 }}>
              {err}
            </div>
          )}
        </div>
      );
    }

    if (type === "imagelink") {
      return <ImageLinkEditor sub={sub} />;
    }

    // Fallback inputs
    const inputType = [
      "text",
      "number",
      "date",
      "time",
      "email",
      "url",
    ].includes(type)
      ? type
      : "text";
    return (
      <input
        {...commonProps}
        type={inputType}
        placeholder={sub.placeholder || sub.label || sub.field}
        value={String(val)}
        onChange={(e) =>
          handleSubFieldChange(sub.field, e.target.value, sub.type)
        }
      />
    );
  };

  /* =================== Render =================== */

  return (
    <div className="formSubarray-group">
      <label className="formSubarray-label">{col.label}</label>

      <div className="array-input-list">
        {items.map((item, index) => (
          <div key={index} className="array-input-item">
            {(col.subFields || []).map((sub) => (
              <div key={sub.field} className="subfield">
                {renderSavedValue(sub, item[sub.field])}
              </div>
            ))}

            <button
              type="button"
              className="btn-editformSubarray-remove"
              onClick={() => removeItem(index)}
              style={{ marginLeft: "10px" }}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div
        className="array-input-formSubarray"
        style={{
          marginTop: "10px",
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
        }}
      >
        {(col.subFields || []).map((sub) => (
          <div key={sub.field} style={{ minWidth: 200 }}>
            {renderEditor(sub)}
          </div>
        ))}

        <button
          type="button"
          className="btn-editformSubarray-add"
          onClick={addItem}
        >
          + Add
        </button>
      </div>
    </div>
  );
};

export default ArrayFieldRenderer;
